import merge from 'lodash.merge'

import { BreakpointTabs, WidgetBreakpointTab, type WidgetSettings } from '@/types'
import { useWidgetStore } from '@/stores/useWidgetStore'

/**
 * Recursively strips null values from an object so that lodash.merge
 * treats them as "not set" rather than overwriting the base value.
 */
function stripNulls<T>(obj: T): Partial<T> {
  if (obj === null || obj === undefined || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj as Partial<T>
  }
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (value === null) continue
    result[key] = typeof value === 'object' && !Array.isArray(value) ? stripNulls(value) : value
  }
  return result as Partial<T>
}

/**
 * Get the breakpoint settings based on the breakpoint.
 * Merges the "all" base with the breakpoint-specific overrides.
 * Null values in breakpoint overrides are treated as "inherit from all".
 */
export function getBreakpointSettings<T>(
  breakpoint: WidgetBreakpointTab,
  designByBreakpoint: Partial<Record<WidgetBreakpointTab, T>>
): T | undefined {
  const allBreakpoint = designByBreakpoint?.all
  if (breakpoint === WidgetBreakpointTab.All) {
    return allBreakpoint
  }
  const specific = designByBreakpoint[breakpoint]
  if (!allBreakpoint && !specific) {
    return undefined
  }
  return merge({}, allBreakpoint, specific ? stripNulls(specific) : {})
}

//  Get the breakpoint width based on the breakpoint
export function getBreakpointWidth(breakpoint: WidgetBreakpointTab) {
  return BreakpointTabs[breakpoint].breakpointWidth
}

// Returns the breakpoint in which the screen width falls based solely on screen width
// IE if screenWidth is 1024, it will return "desktop", if it's 1023, it will return "tablet", if it's 766, it will return "phone"
export function getActualBreakpoint(screenWidth: number): WidgetBreakpointTab {
  const sortedBreakpoints = Object.entries(BreakpointTabs).sort(
    (a, b) => (b[1].breakpointWidth ?? 0) - (a[1].breakpointWidth ?? 0)
  )
  return sortedBreakpoints.find(
    ([_, info]) => screenWidth >= (info.breakpointWidth ?? 0)
  )?.[0] as WidgetBreakpointTab
}

// Returns the breakpoint in which the screen with falls
// IE if screenWidth is 1024, it will return "desktop", if it's 1023, it will return "tablet", if it's 766, it will return "phone"
export function getProperBreakpoint(screenWidth: number) {
  const { isFloatingWidgetMode, floatingWidgetOpen } = useWidgetStore.getState()
  if (isFloatingWidgetMode && floatingWidgetOpen) {
    return WidgetBreakpointTab.Phone
  }

  return getActualBreakpoint(screenWidth)
}

/**
 * Returns the resolved padding from icon settings, treating all-zero padding
 * as unset so that defaults (24px) apply instead.
 */
export function resolveIconPadding(padding: { top?: number; right?: number; bottom?: number; left?: number } | undefined) {
  const DEFAULT_PADDING = 24
  const isAllZero = padding &&
    !padding.top && !padding.right && !padding.bottom && !padding.left

  if (!padding || isAllZero) {
    return { top: DEFAULT_PADDING, right: DEFAULT_PADDING, bottom: DEFAULT_PADDING, left: DEFAULT_PADDING }
  }

  return {
    top: padding.top ?? DEFAULT_PADDING,
    right: padding.right ?? DEFAULT_PADDING,
    bottom: padding.bottom ?? DEFAULT_PADDING,
    left: padding.left ?? DEFAULT_PADDING,
  }
}

// Get the position style based on the position
export function getPositionStyle(position: string) {
  switch (position) {
    case 'top-left':
      return { top: 0, left: 0 }
    case 'top-right':
      return { top: 0, right: 0 }
    case 'bottom-right':
      return { bottom: 0, right: 0 }
    default:
      return { bottom: 0, left: 0 }
  }
}

// Get the default widget settings
export function getDefaultWidgetSettings(): Partial<WidgetSettings> {
  return {
    iconSettings: {
      designByBreakpoint: {
        [WidgetBreakpointTab.All]: {
          height: 48,
          width: 48,
          position: 'bottom-left',
          padding: {
            top: 24,
            right: 24,
            bottom: 24,
            left: 24,
          },
          zIndex: 2147483647,
          borderRadius: {
            topLeft: 16,
            topRight: 16,
            bottomLeft: 16,
            bottomRight: 16,
          },
        },
      },
    },
    welcomeMessageSettings: {
      designByBreakpoint: {
        [WidgetBreakpointTab.All]: {
          enabled: true,
          textStyles: {
            color: '#FFFFFF',
            fontFamily: 'Manrope',
            fontSize: 13,
            fontWeight: '700',
            lineHeight: '20px',
          },
          textContainer: {
            color: '#FFFFFF',
            borderRadius: {
              topLeft: 16,
              topRight: 16,
              bottomLeft: 16,
              bottomRight: 16,
            },
            padding: {
              top: 8,
              right: 20,
              bottom: 8,
              left: 20,
            },
          },
          welcomeMessageType: 'default',
        },
      },
    },
  } as Partial<WidgetSettings>
}
